import * as JsonSchema from '../../utils/json-schema'
import { Chart, AxisMinMax, Axis, Constant, LineOptions, BarOptions, Series, PlotBand } from './charts-api-shared.model'

export type T = ChartsApiResponse

type ChartsApiResponse = ChartsApiResponseItem[]

type ChartsApiResponseItem = Chart & { isOwn: boolean }

const AXIS_MIN_MAX_SCHEMA:JsonSchema.T<AxisMinMax> = {
  oneOf: [
    {
      type      : 'object',
      required  : [ 'type' ],
      properties: { type: { type: 'string', 'const': 'auto' }, }
    },
    {
      type      : 'object',
      required  : [ 'type', 'value' ],
      properties: {
        type : { type: 'string', 'const': 'fixed' },
        value: { type: 'number' }
      }
    }
  ]
}
const AXIS_SCHEMA:JsonSchema.T<Axis> = {
  type      : 'object',
  required  : [ 'unit', 'position', 'min', 'max' ],
  properties: {
    unit    : { type: 'string' },
    position: { type: 'string', 'enum': [ 'auto', 'left', 'right' ] },
    min     : AXIS_MIN_MAX_SCHEMA,
    max     : AXIS_MIN_MAX_SCHEMA,
  },
}
const CONSTANT_SCHEMA:JsonSchema.T<Constant> = {
  type      : 'object',
  required  : [ 'id', 'value', 'unit', 'name', 'color', ],
  properties: {
    id   : { type: 'string' },
    value: { type: 'number' },
    unit : { type: 'string' },
    name : { type: 'string' },
    color: { type: 'string' },
  }
}
const LINE_OPTIONS_SCHEMA: JsonSchema.T<LineOptions> = {
  type      : 'object',
  required  : [ 'type' ],
  properties: {
    type  : { type: 'string', 'const': 'line' },
    color : { type: 'string', nullable: true },
    marker: {
      type    : 'string',
      'enum'  : [ 'arrow', 'circle', 'diamond', 'pin', 'rect', 'roundRect', 'triangle', 'emptyCircle', 'none' ],
      nullable: true 
    },
    markerSize: { type: 'number', nullable: true }
  }
}
const BAR_OPTIONS_SCHEMA: JsonSchema.T<BarOptions> = {
  type      : 'object',
  required  : [ 'type' ],
  properties: {
    type : { type: 'string', 'const': 'bar' },
    color: { type: 'string', nullable: true },
  }
}
const SERIES_SCHEMA:JsonSchema.T<Series> = {
  type      : 'object',
  required  : [ 'id', 'options' ],
  properties: {
    id     : { type: 'string' },
    options: { oneOf: [ LINE_OPTIONS_SCHEMA, BAR_OPTIONS_SCHEMA ] },
  }
}

const PLOT_BAND_SCHEMA:JsonSchema.T<PlotBand> = {
  type      : 'object',
  required  : [ 'type' ],
  properties: { type: { type: 'string', 'enum': [ 'nights', 'weekends' ] } }
}

const CHART_SCHEMA:JsonSchema.T<ChartsApiResponseItem> = {
  type    : 'object',
  required: [
    'id',
    'name',
    'series',
    'constants',
    'axes',
    'plotBands',
    'scopeId',
    'isOwn',
  ],
  properties: {
    id       : { type: 'string' },
    name     : { type: 'string' },
    isOwn    : { type: 'boolean' },
    series   : { type: 'array', items: SERIES_SCHEMA },
    constants: { type: 'array', items: CONSTANT_SCHEMA },
    axes     : { type: 'array', items: AXIS_SCHEMA },
    plotBands: {
      type    : 'array',
      minItems: 0,
      maxItems: 1,
      items   : PLOT_BAND_SCHEMA,
    } as unknown as JsonSchema.T<[PlotBand]|[]>, // can't make AJV type checking work for union with empty array
    visibility             : { type: 'string', 'enum': [	'private', 'public' ], nullable: true },
    scopeId                : { type: 'string' },
    displayCumulativeAsRate: { type: 'boolean', nullable: true },
  }
}

const CHARTS_API_RESPONSE_SCHEMA: JsonSchema.T<ChartsApiResponse> = {
  type : 'array',
  items: CHART_SCHEMA
}

export const parse = JsonSchema.parserForCollection(CHARTS_API_RESPONSE_SCHEMA, 'charts api responses')
